import { FC } from "react";
import { Box, Grid, GridItem, VBox } from "../../components-v2/Box";
import {
  AddToElectionOfBoardModel,
  boardChangeItemIcon,
  boardChangeItemLabel,
  boardChangeSummaryDescription,
  ElectionOfBoardChangeModel,
  ElectionOfBoardDialogState,
  ElectionOfBoardType,
  ElectionOfCompanyBoardForm,
  isPersonalDeputy,
  prepareBoardChangeItems,
  prepareShowBoardTabs,
  RemoveElectionOfBoardModel,
  UpdateElectionOfBoardModel,
} from "./electionOfCompanyBoardDomain";
import { observer } from "mobx-react";

import { useLanguage, useTranslation } from "../../stores/RootStoreContext";
import { getRoleTypeLabel } from "../../types/enums/RoleType";
import { Tabs } from "../../components-v2/Tabs";
import { Checkbox, MCheckboxGroup } from "../../components-v2/Checkbox";
import { YesNoQuestion } from "../common/commonComponents";
import { Markdown } from "../../text/Markdown";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";
import { Icon } from "../../components-v2/Icon";
import { Dialog } from "../../components-v2/Dialog";
import { MTextInput } from "../../components-v2/TextInput";
import { MSelect } from "../../components-v2/Select";
import { norway } from "../../utils/countries";
import { Autocomplete } from "../../components-v2/Autocomplete";
import { PhoneInput } from "../../components-v2/PhoneInput";
import { SelectActorsDialog } from "../../dialogs/dialogComponents";
import { when } from "../../utils/functions";
import { Alert } from "../../components-v2/Alert";

export const ElectionOfCompanyBoardView: FC<{
  form: ElectionOfCompanyBoardForm;
}> = observer(({ form }) => {
  return (
    <VBox gap={"xl"} marginBottom={"lg"}>
      <ElectionOfBoardPreview form={form} />
      <ElectionOfBoardChanges form={form} />
      <ElectionOfBoardOptions form={form} />
      <ElectionOfBoardDialogs state={form.boardChangeDialogState} />
    </VBox>
  );
});

export const ElectionOfBoardChanges = observer(function ({
  form,
}: {
  form: ElectionOfCompanyBoardForm;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  return (
    <VBox>
      {form.containsBoardChanges && (
        <VBox>
          <strong>{t("decisions.electionOfCompanyBoard.boardChange.title")}</strong>
          {form.boardChanges.map((model) => (
            <BoardChangeSummary
              key={model.id}
              model={model}
              onDelete={() => form.showDeleteBoardChangeDialog(model.id)}
              onEdit={() => form.editBoardChange(locale, model)}
              isLocked={form.isProjectLocked}
            />
          ))}
        </VBox>
      )}
      {!form.isReElectionOfBoardMembers && <ElectionOfBoardChangeActions form={form} />}
    </VBox>
  );
});

export const ElectionOfBoardChangeActions = observer(function ({
  form,
}: {
  form: ElectionOfCompanyBoardForm;
}) {
  const locale = useLanguage();
  return (
    <VBox gap={"md"}>
      {!form.containsBoardChanges && (
        <Box>
          <Button
            variant={"basic"}
            size={"sm"}
            icon={boardChangeItemIcon[ElectionOfBoardType.reElectBoardMembers]}
            label={boardChangeItemLabel[ElectionOfBoardType.reElectBoardMembers]}
            onClick={() =>
              form.onSelectBoardChange(locale, ElectionOfBoardType.reElectBoardMembers)
            }
            disabled={form.isProjectLocked}
          />
        </Box>
      )}
      <VBox grow>
        <DropdownMenu
          button={{
            type: "button",
            variant: "secondary",
            disableArrow: true,
            icon: "plus",
            label: { key: "decisions.electionOfCompanyBoard.boardChange.button.label" },
            disabled: form.isProjectLocked,
          }}
          items={prepareBoardChangeItems(locale, form)}
        />
      </VBox>
    </VBox>
  );
});

export const BoardChangeSummary = observer(function ({
  model,
  onDelete,
  onEdit,
  isLocked,
}: {
  model: ElectionOfBoardChangeModel;
  onDelete: () => void;
  onEdit: () => void;
  isLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Grid alignItems={"center"}>
      <GridItem xs={8}>
        <Box alignItems={"center"}>
          <Icon name={boardChangeItemIcon[model.data.type]} />
          <Box>{t(boardChangeSummaryDescription(model.data))}</Box>
        </Box>
      </GridItem>
      <GridItem xs={4}>
        <Box justifyContent={"end"}>
          {![
            ElectionOfBoardType.reElectBoardMembers,
            ElectionOfBoardType.removeAllBoardMembers,
            ElectionOfBoardType.removeBoardMember,
          ].includes(model.data.type) && (
            <LinkButton label={t("edit")} icon={"edit"} onClick={onEdit} disabled={isLocked} />
          )}
          <IconButton icon={"trash"} tooltip={t("delete")} onClick={onDelete} disabled={isLocked} />
        </Box>
      </GridItem>
    </Grid>
  );
});

export const ElectionOfBoardPreview: FC<{
  form: ElectionOfCompanyBoardForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <Tabs scrollable={false} tabs={prepareShowBoardTabs(form)} />
      {form.showCurrentBoard &&
        form.roles.map((member) => (
          <Box gap={"sm"} justifyContent={"space-between"} key={member.companyroleId}>
            <Box>{member.name}</Box>
            <Box>{t(getRoleTypeLabel(member.role))}</Box>
          </Box>
        ))}
      {!form.showCurrentBoard && (
        <VBox gap={"md"}>
          {form.isNewBoardMissingChairperson && (
            <Alert
              icon={"bell"}
              message={t("decisions.electionOfCompanyBoard.rememberChairperson")}
              type={"info"}
            />
          )}
          {form.newCompanyBoard.map((member) => (
            <Box gap={"sm"} justifyContent={"space-between"} key={member.id}>
              <Box>{member.name}</Box>
              <Box>
                {member.personalDeputyFor !== undefined && isPersonalDeputy(member)
                  ? t("decisions.electionOfCompanyBoard.personalDeputyFor", {
                      names:
                        member.personalDeputyFor.length > 2
                          ? member.personalDeputyFor.length
                          : { type: "list", value: member.personalDeputyFor.map((a) => a.name) },
                    })
                  : t(getRoleTypeLabel(member.role))}
              </Box>
            </Box>
          ))}
        </VBox>
      )}
    </VBox>
  );
});

export const ElectionOfBoardOptions: FC<{
  form: ElectionOfCompanyBoardForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"}>
      <strong>{t("options")}</strong>
      <VBox gap={"md"}>
        <Checkbox
          label={{
            key: "decisions.electionOfCompanyBoard.boardMeetingProtocolShowCompanyBoard.label",
          }}
          checked={form.boardMeetingProtocolShowCompanyBoard.value}
          onChange={(event) =>
            form.boardMeetingProtocolShowCompanyBoard.setValue(event.target.checked)
          }
          disabled={form.isProjectLocked || !form.containsBoardMeetingMinutes}
        />
        <Checkbox
          label={{
            key: "decisions.electionOfCompanyBoard.noticeGeneralMeetingShowCompanyBoard.label",
          }}
          checked={form.noticeGeneralMeetingShowCompanyBoard.value}
          onChange={(event) =>
            form.noticeGeneralMeetingShowCompanyBoard.setValue(event.target.checked)
          }
          disabled={form.isProjectLocked || !form.containsNoticeGeneralMeeting}
        />
      </VBox>
      {form.isNorwegianCompany && (
        <VBox>
          <YesNoQuestion
            question={t("decisions.electionOfCompanyBoard.genderCompositionRequirement.question", {
              companyName: form.companyName,
            })}
            field={form.genderCompositionRequirement}
            isLocked={form.isProjectLocked}
            infoText={
              <Markdown
                value={t("decisions.electionOfCompanyBoard.genderCompositionRequirement.info")}
              />
            }
          />
        </VBox>
      )}
    </VBox>
  );
});

export const ElectionOfBoardDialogs = observer(
  ({ state }: { state: ElectionOfBoardDialogState }) => {
    switch (state.type) {
      case "addBoardMember":
        return <AddBoardMemberDialog model={state.model} />;
      case "addBoardMemberFromPortfolio":
        return <SelectActorsDialog model={state.model} />;
      case "updateBoardMember":
        return <UpdateBoardMemberDialog model={state.model} />;
      case "removeBoardMember":
        return <RemoveBoardMemberDialog model={state.model} />;
      default:
        return null;
    }
  },
);

export const AddBoardMemberDialog = observer(({ model }: { model: AddToElectionOfBoardModel }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.title.add" }}
      onCancel={model.onClose}
      onSubmit={model.submit}
      maxWidth={"sm"}
      isSubmitDisabled={!model.isValid}
    >
      <VBox gap={"md"}>
        <Box>{t("decisions.electionOfCompanyBoard.boardChange.dialog.description.add")}</Box>
        <MTextInput
          label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.name" }}
          model={model.name}
        />
        <MSelect
          label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.role" }}
          model={model.roleType}
        />
        {model.isDeputyMember && (
          <VBox>
            <div>{t("decisions.electionOfCompanyBoard.boardChange.dialog.personalDeputyFor")}</div>
            <MCheckboxGroup model={model.personalDeputyFor} />
          </VBox>
        )}
        <MSelect
          label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.nationality" }}
          model={model.nationality}
        />
        <MTextInput
          label={{
            key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber",
          }}
          model={model.socialSecurityNumber}
        />
        {model.nationality.value.code !== norway && (
          <MTextInput
            label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.dnr" }}
            model={model.dnr}
          />
        )}
        <MTextInput
          label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.email" }}
          model={model.email}
        />
        <PhoneInput model={model.phoneNumber} />
      </VBox>
    </Dialog>
  );
});

export const UpdateBoardMemberDialog = observer(
  ({ model }: { model: UpdateElectionOfBoardModel }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={
          model?.data !== undefined
            ? model.data.name
            : { key: "decisions.electionOfCompanyBoard.boardChange.dialog.title.update" }
        }
        onCancel={model.onClose}
        onSubmit={model.submit}
        maxWidth={"sm"}
      >
        <VBox gap={"md"}>
          {!model.isEdit && (
            <>
              <Autocomplete
                label={t("decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember")}
                placeholder={t(
                  "decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember.placeholder",
                )}
                value={model.selectedMember.value}
                error={!model.selectedMember.isValid}
                message={when(model.selectedMember.error, t)}
                items={model.selectedMember.options}
                idFn={model.selectedMember.params.idFn}
                labelFn={(item) => t(model.selectedMember.params.labelFn(item))}
                onSelect={model.selectedMember.setValue}
                onClear={() => model.selectedMember.setValue(null)}
                disabled={model.isEdit}
              />
            </>
          )}
          <MSelect
            label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.role.new" }}
            model={model.roleType}
          />
          {model.isDeputyMember && (
            <VBox>
              <div>
                {t("decisions.electionOfCompanyBoard.boardChange.dialog.personalDeputyFor")}
              </div>
              <MCheckboxGroup model={model.personalDeputyFor} />
            </VBox>
          )}
          <MSelect
            label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.nationality" }}
            model={model.nationality}
          />
          <MTextInput
            label={{
              key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber",
            }}
            model={model.socialSecurityNumber}
          />
          {model.nationality.value.code !== norway && (
            <MTextInput
              label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.dnr" }}
              model={model.dnr}
            />
          )}
        </VBox>
      </Dialog>
    );
  },
);

export const RemoveBoardMemberDialog = observer(
  ({ model }: { model: RemoveElectionOfBoardModel }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.title.remove" }}
        onCancel={model.onClose}
        onSubmit={model.submit}
        maxWidth={"sm"}
      >
        <VBox gap={"md"}>
          <Autocomplete
            label={t("decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember")}
            placeholder={t(
              "decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember.placeholder",
            )}
            value={model.selectedMember.value}
            error={!model.selectedMember.isValid}
            message={when(model.selectedMember.error, t)}
            items={model.selectedMember.options}
            idFn={model.selectedMember.params.idFn}
            labelFn={(item) => t(model.selectedMember.params.labelFn(item))}
            onSelect={model.selectedMember.setValue}
            onClear={() => model.selectedMember.setValue(null)}
          />
        </VBox>
      </Dialog>
    );
  },
);
